import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  ReferenceLine,
} from 'recharts';
import { chartColors, formatCurrency, formatCurrencyCompact } from './chartUtils';

export interface BarChartData {
  name: string;
  value: number;
  value2?: number;
  limit?: number;
  color?: string;
}

interface BarChartProps {
  data: BarChartData[];
  title?: string;
  layout?: 'horizontal' | 'vertical';
  showGrid?: boolean;
  showLegend?: boolean;
  barSize?: number;
  stacked?: boolean;
  label1?: string;
  label2?: string;
  showLimits?: boolean;
  height?: number;
  colorByValue?: boolean; // Color bars based on over/under limit
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    fill: string;
    dataKey: string;
    payload: BarChartData;
  }>;
  label?: string;
  label1?: string;
  label2?: string;
}

function CustomTooltip({ active, payload, label, label1, label2 }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  const data = payload[0].payload;

  return (
    <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-stone/10">
      <p className="font-medium text-charcoal mb-2">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.fill }}
          />
          <span className="text-sm text-stone">
            {entry.dataKey === 'value' ? label1 || 'Actual' : label2 || 'Budget'}:
          </span>
          <span className="font-medium text-charcoal tabular-nums">
            {formatCurrency(entry.value)}
          </span>
        </div>
      ))}
      {data.limit && (
        <div className="mt-2 pt-2 border-t border-stone/10">
          <span className="text-sm text-stone">Limit: </span>
          <span className="font-medium text-charcoal tabular-nums">
            {formatCurrency(data.limit)}
          </span>
          {data.value > data.limit && (
            <span className="ml-2 text-sm text-danger">
              ({formatCurrency(data.value - data.limit)} over)
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function BarChart({
  data,
  title,
  layout = 'vertical',
  showGrid = true,
  showLegend = false,
  barSize = 24,
  stacked = false,
  label1 = 'Actual',
  label2 = 'Budget',
  showLimits = false,
  height = 300,
  colorByValue = false,
}: BarChartProps) {
  const hasSecondValue = data.some(d => d.value2 !== undefined);

  // Assign colors if not provided
  const coloredData = data.map((item, index) => {
    let color = item.color || chartColors.primary[index % chartColors.primary.length];

    // Override color if colorByValue is enabled and item has a limit
    if (colorByValue && item.limit !== undefined) {
      if (item.value > item.limit) {
        color = chartColors.expense;
      } else if (item.value > item.limit * 0.8) {
        color = '#FFA726'; // warning
      } else {
        color = chartColors.income;
      }
    }

    return { ...item, color };
  });

  const isHorizontal = layout === 'horizontal';

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-lg font-semibold text-charcoal mb-4">{title}</h3>
      )}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={coloredData}
            layout={layout}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            {showGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E5E7EB"
                vertical={isHorizontal}
                horizontal={!isHorizontal}
              />
            )}
            {isHorizontal ? (
              <>
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#8B8589', fontSize: 12 }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#8B8589', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatCurrencyCompact}
                />
              </>
            ) : (
              <>
                <XAxis
                  type="number"
                  tick={{ fill: '#8B8589', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatCurrencyCompact}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: '#8B8589', fontSize: 12 }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={false}
                  width={100}
                />
              </>
            )}
            <Tooltip
              content={<CustomTooltip label1={label1} label2={label2} />}
              cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
            />
            {showLegend && <Legend />}

            <Bar
              dataKey="value"
              name={label1}
              barSize={barSize}
              radius={[4, 4, 4, 4]}
              stackId={stacked ? 'stack' : undefined}
            >
              {coloredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>

            {hasSecondValue && (
              <Bar
                dataKey="value2"
                name={label2}
                barSize={barSize}
                fill={chartColors.primary[1]}
                radius={[4, 4, 4, 4]}
                stackId={stacked ? 'stack' : undefined}
                opacity={0.6}
              />
            )}

            {/* Reference lines for limits */}
            {showLimits && coloredData.map((item, index) => (
              item.limit ? (
                <ReferenceLine
                  key={`limit-${index}`}
                  x={isHorizontal ? undefined : item.limit}
                  y={isHorizontal ? item.limit : undefined}
                  stroke="#EF5350"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                />
              ) : null
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
